import { IconBrandGoogle } from "@assets/icons";
import { RegistrationMethod, useStatsigTracking } from "@configs/statsig";
import { CredentialResponse, GoogleLogin } from "@react-oauth/google";
import { Button, PropsWithClassName, cn } from "kz-ui-sdk";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { AuthSocialErrors, AuthSocialProviderProps } from "../types";
import styles from "./GoogleAuthButton.module.css";

const PROVIDER_NAME = "Google";

interface GoogleAuthButtonProps extends AuthSocialProviderProps, PropsWithClassName {
  loading?: boolean;
}

const GoogleAuthButton = ({
  onOAuthLoginSuccess,
  onOAuthLoginError,
  mode,
  loading,
  className,
}: GoogleAuthButtonProps) => {
  const { t } = useTranslation();
  const { trackRegisterFormFocus } = useStatsigTracking();

  const handleOnSuccess = (credentialResponse: CredentialResponse) => {
    const idToken = credentialResponse.credential;
    if (!idToken) {
      onOAuthLoginError?.(AuthSocialErrors.SignInThirdPartyFailed);
      return;
    }
    onOAuthLoginSuccess?.(idToken);
  };

  const handleWrapperClick = () => {
    // Track registration form focus when user clicks Google auth button in register mode
    // This fires on click before OAuth flow completes
    if (mode === "register") {
      trackRegisterFormFocus(RegistrationMethod.GOOGLE_LOGIN);
    }
  };

  const btnText = useMemo(() => {
    const translationKey = mode === "login" ? "login with {{provider}}" : "sign up with {{provider}}";
    return t(translationKey, { provider: PROVIDER_NAME });
  }, [mode, t]);

  return (
    <div
      className={cn("relative w-full", className)}
      onClick={handleWrapperClick}
    >
      <div
        className={cn("absolute left-0 top-0 h-full w-full overflow-hidden opacity-0", {
          "pointer-events-none": loading,
        })}
      >
        <GoogleLogin
          onSuccess={handleOnSuccess}
          onError={onOAuthLoginError}
          containerProps={{
            className: styles.btnGoogleAuth,
          }}
          width="400px"
        />
      </div>

      <Button
        variant="secondary"
        size="lg"
        icon={<IconBrandGoogle className="h-6 w-6" />}
        classes={{
          "label&": "text-content-base text-base font-normal",
        }}
        aria-label={`OAuth ${mode} with ${PROVIDER_NAME}`}
        loading={loading}
      >
        {btnText}
      </Button>
    </div>
  );
};

export default GoogleAuthButton;
